import { Component, Input, OnInit } from '@angular/core';
import { CustomerTag } from '../../../../../../models/customer';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { batchAll, CallbackResult, CallbackResultStatus, searchStringList } from '../../../../../../utils';
import { CustomerService } from '../../../../../../services/customer/customer.service';
import { ChallengeService } from '../../../../../../services/challenge/challenge.service';
import { Challenge, ChallengeStatus } from '../../../../../../models/challenge';
import { AccountService } from '../../../../../../services/account/account.service';

@Component({
  selector: 'app-bulk-add-or-remove-customer-challenge-modal',
  templateUrl: './bulk-add-or-remove-customer-challenge-modal.component.html',
  styleUrls: ['./bulk-add-or-remove-customer-challenge-modal.component.css']
})
export class BulkAddOrRemoveCustomerChallengeModalComponent implements OnInit {

  @Input() customerIds: number[];
  @Input() isAdd: boolean;
  @Input() savedCallback: (result: CallbackResult) => undefined;

  public selectedChallenge: Challenge = null;

  public challengeList: Challenge[];

  public errorMessage = '';
  public serverError: number = null;

  public loadingChallenges = false;
  public loadingSave = false;
  public numberSaved = 0;

  constructor(
    public activeModal: NgbActiveModal,
    private accountService: AccountService,
    private challengeService: ChallengeService
  ) {}

  ngOnInit() {
    this.loadChallenges();
  }

  public handleSubmit() {
    this.errorMessage = '';
    this.loadingSave = true;

    this.numberSaved = 0;
    let successfulCount = 0;
    let superfluousCount = 0;

    const customerIdHandler = (customerId: number): Promise<void> => {
      // Stop early if an error message is produced.
      if (this.errorMessage !== '') {
        return Promise.resolve();
      }

      let savePromise: Promise<void>;
      if (this.isAdd) {
        savePromise = this.challengeService.addCustomerToChallenge(customerId, this.selectedChallenge.id);
      } else {
        savePromise = this.challengeService.removeCustomerFromChallenge(customerId, this.selectedChallenge.id);
      }

      return savePromise
        .then(() => { successfulCount++; })
        .catch(error => {
          if (error.status === 400) {
            superfluousCount++;
          } else if (error.status === 403) {
            this.errorMessage = 'You do not have permission to perform this operation.';
          } else if (error.status >= 500) {
            this.serverError = error.status;
          }
        })
        .finally(() => { this.numberSaved++; });
    };

    batchAll(
      4,
      this.customerIds,
      customerIdHandler
    )
      .then(() => {
        if (this.errorMessage !== '') { return; }

        let successMessage = '';
        if (successfulCount > 0) {
          successMessage = successfulCount.toString() + ' customer'
            + (successfulCount > 1 ? 's' : '') + ' successfully '
            + (this.isAdd ? 'added to ' : 'removed from ')
            + this.selectedChallenge.name + '.';
        }
        if (superfluousCount > 0) {
          successMessage += ' ' + superfluousCount.toString() + ' customer'
            + (superfluousCount > 1 ? 's' : '')
            + (this.isAdd ? ' already had the challenge.' : ' already did not have the challenge.');
        }

        this.savedCallback({
          text: successMessage.trim(),
          status: CallbackResultStatus.success
        });
        this.activeModal.close();
      })
      .finally(() => { this.loadingSave = false; });
  }

  public saveButtonEnabled() {
    return (
      (this.selectedChallenge !== null) &&
      !this.loadingChallenges &&
      !this.loadingSave
    );
  }

  public getLoadingProgressPercentageStr() {
    return Math.ceil((this.numberSaved / this.customerIds.length) * 100).toString();
  }

  private loadChallenges() {
    this.loadingChallenges = true;

    this.challengeService
      .getChallenges(1, 100, {status: ChallengeStatus.active})
      .then(response => {
        this.challengeList = response.results;
        if (this.challengeList.length > 0) {
          this.selectedChallenge = this.challengeList[0];
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingChallenges = false; });
  }

}
